/**
 * `#uc2Similar`: "Most alike to {name}" (client §3.2), the three watersheds most alike to
 * the focused one, as one table on the tab's shared column rhythm (client, 7 October 2026).
 *
 * One row per candidate: its name (with the `Learning opportunity` tag under it where the
 * engine flagged one), how alike it is as a bar with the percentage at its end, its own
 * change status as a badge, the chosen practice's adoption for the candidate against the
 * focused watershed as two mono numbers and the signed gap, and a compact Compare button
 * that opens `#uc2Compare` below the table.
 *
 * Under the table, ONE short note replaces the per-row notes the 2 October round carried:
 * what the block is, and what to look out for across the three, a sentence each.
 *
 * Everything here is precomputed on the focused watershed's own detail payload, so changing
 * the practice group costs no request.
 */
import { useTranslation } from "react-i18next";

import { ChartFrame } from "../../components/charts/ChartFrame";
import type { IntelSimilarRow } from "../../lib/api";
import { BarReading } from "./Uc2TableParts";
import { alikePct, pctText, signedText, similarSummary } from "./uc2Table";
import { UC2_CATEGORY_COLORS } from "./uc2Constants";

export interface SimilarStripProps {
  /** The clicked watershed: the reference every row is read against. */
  reference: {
    id: string;
    name: string;
    category?: string | null;
    adoption: number | null;
  };
  rows: IntelSimilarRow[];
  /**
   * One candidate's own class, from the units rows the pane holds: `IntelSimilarRow`
   * carries none.
   */
  categoryOf: (unitId: string) => string | null;
  /** That group's label, and the same word lower-cased for mid-sentence use. */
  practiceLabel: string;
  practiceLower: string;
  /** Kept for the call site's shape; the table reads no programme mean. */
  programmeMean?: number | null;
  /** `#uc2Compare`: which candidate is open beside the reference, if any. */
  compareWith: string | null;
  onCompare: (unitId: string) => void;
}

export function SimilarStrip({
  reference,
  rows,
  categoryOf,
  practiceLabel,
  practiceLower,
  compareWith,
  onCompare,
}: SimilarStripProps) {
  const { t } = useTranslation();
  const shown = rows.slice(0, 3);
  const na = t("intel.uc2.table.na");

  /** "A", "A and B", "A, B and C", in the reader's language. */
  const listOf = (names: string[]) =>
    names.length < 2
      ? (names[0] ?? "")
      : `${names.slice(0, -1).join(", ")} ${t("intel.uc2.table.and")} ${names[names.length - 1]}`;

  const summary = similarSummary(shown);
  const lookOut =
    summary.kind === "learning" || summary.kind === "more"
      ? t(
          `intel.uc2.note.similarSum${summary.kind === "learning" ? "Learning" : "More"}`,
          {
            names: listOf(summary.names),
            count: summary.names.length,
            practice: practiceLower,
            name: reference.name,
          },
        )
      : t(
          `intel.uc2.note.similarSum${summary.kind === "noValue" ? "NoValue" : "Similar"}`,
          {
            practice: practiceLower,
            name: reference.name,
          },
        );

  const table = (expanded: boolean) => (
    <div className="uc2-tablewrap">
      <table className="uc2-table is-stack uc2-table-similar">
        <thead>
          <tr>
            <th scope="col" className="c-name">
              {t("intel.uc2.table.watershed")}
            </th>
            <th scope="col" className="c-bar">
              {t("intel.uc2.table.alike")}
            </th>
            <th scope="col" className="c-sub c-status">
              {t("intel.uc2.table.status")}
            </th>
            <th scope="col" className="c-val">
              {t("intel.uc2.table.adoptionVs", {
                practice: practiceLabel,
                name: reference.name,
              })}
            </th>
            <th scope="col" className="c-act">
              <span className="sr-only">{t("intel.uc2.compareBtn")}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {shown.map((row) => {
            const pct = alikePct(row.similarity);
            const category = categoryOf(row.unit_id) ?? "not_assessed";
            const gap =
              typeof row.gap === "number" && Number.isFinite(row.gap)
                ? row.gap
                : null;
            return (
              <tr
                key={row.unit_id}
                data-testid={
                  expanded ? undefined : `uc2-similar-${row.unit_id}`
                }
              >
                <td className="c-name">
                  <span className="uc2-name">{row.name}</span>
                  {row.learning_opportunity ? (
                    <span
                      className="uc2-tag"
                      data-testid={`uc2-similar-tag-${row.unit_id}`}
                    >
                      {t("intel.uc2.learningOpportunity")}
                    </span>
                  ) : null}
                </td>
                <td className="c-bar">
                  <BarReading
                    value={pct / 100}
                    text={pctText(pct)}
                    label={t("intel.uc2.table.alikeAria", { pct })}
                    testId={
                      expanded ? undefined : `uc2-similar-alike-${row.unit_id}`
                    }
                  />
                </td>
                <td className="c-sub c-status">
                  <span
                    className="uc2-status"
                    data-category={category}
                    style={{
                      borderColor:
                        UC2_CATEGORY_COLORS[category] ?? "var(--line)",
                    }}
                  >
                    {t(`intel.uc2.category.${category}`, {
                      defaultValue: category,
                    })}
                  </span>
                </td>
                <td
                  className="c-val"
                  data-testid={
                    expanded ? undefined : `uc2-similar-adoption-${row.unit_id}`
                  }
                >
                  <span className="uc2-num">
                    {row.adoption != null ? Math.round(row.adoption) : na}
                  </span>
                  <span className="uc2-vs"> {t("intel.uc2.table.vs")} </span>
                  <span className="uc2-num">
                    {reference.adoption != null
                      ? Math.round(reference.adoption)
                      : na}
                  </span>
                  {gap != null ? (
                    // `gap` is reference minus candidate; the cell reads candidate minus
                    // reference, so the sign says which way the candidate sits.
                    <span className="uc2-num uc2-delta">
                      {signedText(-gap)}
                    </span>
                  ) : null}
                </td>
                <td className="c-act">
                  <button
                    type="button"
                    className="btn ghost sm uc2-compare-btn"
                    id={expanded ? undefined : `uc2Compare-${row.unit_id}`}
                    aria-pressed={compareWith === row.unit_id}
                    onClick={() => onCompare(row.unit_id)}
                  >
                    {t("intel.uc2.compareBtn")}
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  const title = t("intel.uc2.similarToTitle", { name: reference.name });
  return (
    <div id="uc2Similar" className="result-box">
      <h5>{title}</h5>
      <div className="result-pad">
        {shown.length ? (
          <>
            <ChartFrame
              title={title}
              expandLabel={t("intel.uc2.table.expand")}
              testId="uc2-similar-table"
            >
              {(size) => table(size.expanded)}
            </ChartFrame>
            {/* One sentence for what the block is, one for what to look out for: a line
                each, in place of the note block every row used to carry. */}
            <p className="uc2-table-say" data-testid="uc2-similar-note">
              <span className="uc2-say-line">
                {t("intel.uc2.note.similarHappening", { name: reference.name })}
              </span>{" "}
              <span className="uc2-say-line">{lookOut}</span>
            </p>
          </>
        ) : (
          <p className="uc2-quiet">{t("intel.uc2.similarEmpty")}</p>
        )}
      </div>
    </div>
  );
}

export default SimilarStrip;
